import { SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import Button from "@/components/Button/Button";
import {
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS
} from "@/components/Button/types";
import { MenuAnchorEl, ROUTES, useMediaQueries } from "@/shared";
import RectangleLoader from "@/shared/components/loaders/RectangleLoader";
import { useDrawerStore } from "@/stores/useDrawerStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import { FilterOutlineIcon } from "@dotnet/telematics-design-system";

import { useStyles } from "../styles";

interface FilterButtonProps {
  route: ROUTES;
  onOpen: (e: { currentTarget: SetStateAction<MenuAnchorEl> }) => void;
  hasLoadingCustomStyle?: boolean;
  hasMoreFilters?: boolean;
  moreFiltersBreakPoint?: boolean;
}

const FilterButton = ({
  route,
  onOpen,
  hasLoadingCustomStyle,
  hasMoreFilters = false,
  moreFiltersBreakPoint
}: FilterButtonProps) => {
  const { t } = useTranslation();
  const {
    initialLoading,
    params: { desktopDetailsSection }
  } = useTableStoreByRoute(route);

  const isDesktopDrawerOpen = useDrawerStore(
    state => state.isDesktopDrawerOpen
  );

  const { toSm, toLg, to1099, fromMd, fromLg } = useMediaQueries();

  const { filterButtonLoader } = useStyles({
    toSm,
    toLg,
    hasLoadingCustomStyle
  });

  const isDetailsAndDrawerOpen = () => {
    return desktopDetailsSection && isDesktopDrawerOpen;
  };

  const buttonWithMoreFilters =
    hasMoreFilters &&
    (moreFiltersBreakPoint || fromLg) &&
    !isDetailsAndDrawerOpen();

  const rectangularWidth = toLg ? 75 : 112;

  return initialLoading ? (
    <RectangleLoader
      testId="filter-button-loader"
      width={rectangularWidth}
      height={24}
      customStyle={filterButtonLoader}
    />
  ) : (
    <Button
      data-testid="filter-button"
      color={BUTTON_COLORS.White}
      size={BUTTON_SIZES.Small}
      variant={
        to1099 && fromMd ? BUTTON_VARIANTS.IconOnly : BUTTON_VARIANTS.IconLeft
      }
      icon={<FilterOutlineIcon />}
      onClick={onOpen}
    >
      {buttonWithMoreFilters ? t("Button##more filters") : t("Button##filters")}
    </Button>
  );
};

export default FilterButton;
